import { Controller, useWatch, type Control } from 'react-hook-form';
import { Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { AdvanceSlider } from './VoiceAgentAdvanceSlider';

const VAD_STEP = 0.1;

interface VoiceAgentVadSectionProps {
  control: Control<EditAgentFormValues>;
}

export function VoiceAgentVadSection({ control }: Readonly<VoiceAgentVadSectionProps>) {
  const enabled = useWatch({ control, name: 'vad.enabled', defaultValue: false });

  return (
    <div className="rounded-xl border border-divider bg-content1">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-default-100 p-2 text-primary">
            <Icon icon="solar:microphone-linear" width={20} />
          </div>
          <div>
            <p className="font-medium text-foreground">Voice Activity Detection</p>
            <p className="mt-0.5 text-sm text-default-500">
              Tune when the agent detects speech start and stop.
            </p>
          </div>
        </div>
        <Controller
          name="vad.enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              aria-label="Enable voice activity detection"
              isSelected={field.value ?? false}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>

      {enabled && (
        <div className="space-y-5 border-t border-divider p-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Controller
              name="vad.confidence"
              control={control}
              defaultValue={0.7}
              render={({ field }) => (
                <AdvanceSlider
                  label="Confidence"
                  minValue={0}
                  maxValue={0.7}
                  step={VAD_STEP}
                  value={field.value ?? 0.7}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="vad.start_secs"
              control={control}
              defaultValue={0.2}
              render={({ field }) => (
                <AdvanceSlider
                  label="Start seconds"
                  minValue={0}
                  maxValue={2}
                  step={VAD_STEP}
                  value={field.value ?? 0.2}
                  onChange={field.onChange}
                  valueSuffix=" s"
                />
              )}
            />
            <Controller
              name="vad.stop_secs"
              control={control}
              defaultValue={0.8}
              render={({ field }) => (
                <AdvanceSlider
                  label="Stop seconds"
                  minValue={0}
                  maxValue={5}
                  step={VAD_STEP}
                  value={field.value ?? 0.8}
                  onChange={field.onChange}
                  valueSuffix=" s"
                />
              )}
            />
            <Controller
              name="vad.min_volume"
              control={control}
              defaultValue={0.6}
              render={({ field }) => (
                <AdvanceSlider
                  label="Min volume"
                  minValue={0}
                  maxValue={1}
                  step={VAD_STEP}
                  value={field.value ?? 0.6}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <Controller
            name="vad.auto_adjust_for_turn_detection"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <div className="flex items-center justify-between gap-4 rounded-lg bg-default-50 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Auto adjust for turn detection
                  </p>
                  <p className="text-xs text-default-500">
                    Automatically tune VAD for natural conversation turns.
                  </p>
                </div>
                <Switch
                  aria-label="Auto adjust for turn detection"
                  isSelected={field.value ?? false}
                  onValueChange={field.onChange}
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
